/**
 * Manages the visibility of scroll buttons within a scrollable container based on
 * the current scroll position. This functionality is particularly useful for UI
 * components like a MenuSelector, where the amount of content may exceed the viewport
 * of the container, needing the use of scroll buttons to navigate through the
 * content.
 *
 * The component hides or shows the left and right press-to-scroll buttons based on
 * whether additional content is available to the left or right of the current viewport.
 * This determination is made client-side by measuring the width of the scrollable
 * content against the viewport width of the container. If the content's width does not
 * exceed the viewport's width, indicating that all content is visible without the need
 * to scroll, the buttons are hidden.
 *
 *
 * ## Example
 *
 *    <div
 *      id="menu-selector-container"
 *      phx-hook="HidePressToScroll"
 *      data-scroll-box-id="menu-selector-bar-scroll-box"
 *      data-scroll-right-button-id="menu-selector-bar-scroll-right-button"
 *      data-scroll-right-button-id="menu-selector-bar-scroll-left-button"
 *     >
 */

export const HidePressToScroll = {
  mounted() {
    this.addScrollEventListener();
    this.hideScrollButton();
  },

  updated() {
    this.addScrollEventListener();
    this.hideScrollButton();
  },

  destroy() {
    this.removeScrollEventListener();
  },

  addScrollEventListener() {
    const scrollBox = document.getElementById(this.el.dataset.scrollBoxId);
    if (scrollBox) {
      scrollBox.removeEventListener("scroll", this.hideScrollButtonBound);
      this.hideScrollButtonBound = this.hideScrollButton.bind(this);

      // Add the event listener for the scroll event
      scrollBox.addEventListener("scroll", this.hideScrollButtonBound);
    }
  },

  removeScrollEventListener() {
    const scrollBox = document.getElementById(this.el.dataset.scrollBoxId);
    if (scrollBox && this.hideScrollButtonBound) {
      scrollBox.removeEventListener("scroll", this.hideScrollButtonBound);
    }
  },

  hideScrollButton() {
    if (this.el.dataset.menuDiscoveryVariant !== "scroll-buttons") {
      return;
    }
    const scrollBox = document.getElementById(this.el.dataset.scrollBoxId);
    const scrollRightButton = document.getElementById(
      this.el.dataset.scrollRightButtonId,
    );
    const scrollLeftButton = document.getElementById(
      this.el.dataset.scrollLeftButtonId,
    );

    if (scrollBox.scrollWidth - scrollBox.scrollLeft > scrollBox.offsetWidth) {
      scrollRightButton.style.display = "block";
    } else {
      scrollRightButton.style.display = "none";
    }

    if (scrollBox.scrollLeft > 0) {
      scrollLeftButton.style.display = "block";
    } else {
      scrollLeftButton.style.display = "none";
    }
  },
};

export default HidePressToScroll;
